import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";

const TEAM = [
  {
    id: "1",
    name: "Amod Singh",
    role: "Principal Architect",
    photo: "/team/amod-singh.jpg",
    bio: "A young, dynamic architect working in the field since 2015 after completing his Bachelor of Architecture from Apeejay School of Planning and Architecture. With special expertise in Architecture, Landscape and Interior, he has designed and supervised corporate offices, commercial projects, retail outlets, and residential housing projects under the guidance of renowned architects like Ar. Gian P Mathur, Ar. Romesh Sapra, and Ar. Ashudeep Raina.",
    contact: "+91 9560171279",
  },
  {
    id: "2",
    name: "Aanchal Sharma",
    role: "Principal Architect",
    photo: "/team/aanchal-sharma.jpg",
    bio: "A Bachelor of Architecture graduate from Apeejay School of Planning and Architecture, she specialises in interior design and has delivered various residential projects and retail outlets. She combines her skills in anthropology, management, and architecture to produce exemplary results. Her innovative methods and strong interpersonal skills add immense value to the firm's market reputation.",
    contact: "+91 8595871172",
  },
];

const VALUES = [
  { label: "Vision", text: "To develop sensible, imaginative, and innovative architecture that elevates India's design landscape while embracing sustainability and craftsmanship." },
  { label: "Mission", text: "To deliver exceptional design and construction solutions through research-driven innovation, technical precision, and strong client collaboration." },
  { label: "Commitment", text: "We expand relationships with clients and partners by remaining true to our values — transparency, creativity, and excellence." },
];

const CAPABILITIES = [
  { icon: "🏛", title: "Architecture", desc: "Innovative and contextually responsive architectural solutions across commercial, residential, and institutional spaces." },
  { icon: "🛋", title: "Interior Design", desc: "Interiors that blend aesthetics, comfort, and functionality — tailored to each client's identity and purpose." },
  { icon: "🏗", title: "Construction & Project Management", desc: "End-to-end execution and project supervision ensuring timelines, quality, and cost efficiency." },
  { icon: "🌿", title: "Landscape Design", desc: "Integrating natural and built environments through thoughtful landscape planning and execution." },
  { icon: "📐", title: "Design & Build", desc: "Unified design and construction services for seamless delivery and full accountability." },
];

export default function About() {
  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="pt-40 pb-16 px-6 lg:px-12" style={{ background: "var(--color-bg-soft)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-xs tracking-widest uppercase text-gray-400 mb-3">Who We Are</div>
          <h1
            className="text-4xl md:text-5xl font-normal text-gray-900 max-w-2xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            About Ark Design International
          </h1>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-start">
          <div>
            <div className="text-xs tracking-widest uppercase text-gray-400 mb-4">Our Story</div>
            <h2
              className="text-3xl font-normal text-gray-900 mb-6"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              Innovation in Every Dimension
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              Ark Design International is a multidisciplinary firm delivering architectural, interior, and landscape design solutions across residential, commercial, retail, and hospitality sectors. Based in Noida, we have successfully completed over 200+ projects across India — from flagship showrooms to bespoke residential designs.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              We believe great design emerges from debate, collaboration, and a deep respect for context — blending creativity with practicality. Our team of young and experienced architects and engineers are committed to excellence, sustainability, and innovation in every project.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Trusted by industry leaders including Kia Motors, Hero MotoCorp, MG Motors, Okinawa Autotech, and Volvo Eicher, we have built long-term partnerships founded on reliability, quality, and consistent delivery.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {VALUES.map(v => (
              <div key={v.label} className="border-l-2 border-gray-900 pl-5">
                <div className="text-xs tracking-widest uppercase text-gray-400 mb-1">Our {v.label}</div>
                <p className="text-sm text-gray-700 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Team */}
      <section className="py-20 px-6 lg:px-12" style={{ background: "var(--color-bg-soft)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-xs tracking-widest uppercase text-gray-400 mb-3">The People</div>
          <h2
            className="text-3xl font-normal text-gray-900 mb-12"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Our Core Team
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {TEAM.map(member => (
              <div key={member.id} className="bg-white border border-gray-100 overflow-hidden flex flex-col sm:flex-row">
                <div className="w-full sm:w-48 flex-shrink-0 bg-gray-100">
                  <img
                    src={member.photo}
                    alt={member.name}
                    className="w-full h-56 sm:h-full object-cover object-top"
                  />
                </div>
                <div className="p-6 flex flex-col justify-between">
                  <div>
                    <h3
                      className="text-xl font-medium text-gray-900 mb-0.5"
                      style={{ fontFamily: "var(--font-heading)" }}
                    >
                      {member.name}
                    </h3>
                    <div className="text-xs tracking-widest uppercase text-gray-400 mb-4">{member.role}</div>
                    <p className="text-sm text-gray-600 leading-relaxed">{member.bio}</p>
                  </div>
                  <a
                    href={`tel:${member.contact.replace(/\s/g, "")}`}
                    className="mt-5 text-xs tracking-widest uppercase font-medium text-gray-900 hover:text-gray-600 transition-colors"
                  >
                    {member.contact}
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Supporting team summary */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { label: "Principal Architects", count: "4", desc: "Combining creativity, technical expertise, and years of industry experience." },
              { label: "Interior Design Specialists", count: "2", desc: "Transforming spaces into experiences with focus on detail and functionality." },
              { label: "Draftsmen & Engineers", count: "1+", desc: "Ensuring design precision, technical accuracy, and flawless execution." },
            ].map(s => (
              <div key={s.label} className="bg-white border border-gray-100 p-6">
                <div
                  className="text-3xl font-semibold text-gray-900 mb-1"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  {s.count}
                </div>
                <div className="text-xs tracking-widest uppercase text-gray-500 mb-2">{s.label}</div>
                <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-xs tracking-widest uppercase text-gray-400 mb-3">What We Do</div>
          <h2
            className="text-3xl font-normal text-gray-900 mb-12"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Our Expertise
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CAPABILITIES.map(c => (
              <div key={c.title} className="border border-gray-100 p-7 hover:border-gray-300 hover:shadow-sm transition-all">
                <div className="text-3xl mb-4">{c.icon}</div>
                <h3
                  className="text-base font-medium text-gray-900 mb-2"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  {c.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
